import React from 'react'
import {Helmet} from 'react-helmet'
import {Route, Routes, useParams} from 'react-router-dom'
import {pathForRoute, routes, routesWithValue} from 'routes/routes'
import {NavigateLocalized} from 'i18n/NavigateLocalized'
import {useRoutesLocationUnderLocale} from 'i18n/localePaths'
import List from 'organization/List'
import OrganizationsProvider from 'organization/providers/OrganizationsProvider'
import {Organization} from 'organization/models/Organization'
import OrganizationProvider from 'organization/providers/OrganizationProvider'
import {useURLHasOrganizationID} from 'utils/lib/url'
import {EventModel} from 'event/models/Event'
import {EventIndex} from 'organization/Event'
import {EventDesign} from 'organization/Event/Design'
import OrganizationEventProvider from 'organization/Event/OrganizationEventProvider'
import {Billing} from 'organization/Billing'
import OrganizationEventsListProvider from 'organization/ListEvents/providers/OrganizationEventsListProvider'
import {EventsList} from 'organization/ListEvents'
import {CreateEvent} from 'organization/CreateEvent'
import {Settings} from 'organization/Event/Settings'
import {Addons} from 'organization/Event/Addons'
import {Tickets} from 'organization/Event/Tickets'
import {Earning} from 'organization/Event/Earning'
import {useOrganizationAuth} from 'organization/Auth/Providers/OrganizationAuthProvider'
import {Login} from 'organization/Auth/Login'
import {Register} from 'organization/Auth/Register'
import {VerifyEmail as OrganizationVerifyEmail} from 'organization/Auth/VerifyEmail'
import EventAttendantsProvider from 'organization/Event/Attendants/EventAttendantsProvider'
import {Attendants} from 'organization/Event/Attendants'
import {ViewAttendant} from 'organization/Event/Attendants/ViewAttendant'
import EventEarningProvider from 'organization/Event/Earning/EventEarningProvider'
import {GoogleCallback} from 'organization/Auth/GoogleCallback'
import {PasswordRecover} from 'organization/Auth/PasswordRecover'
import {Profile} from 'organization/Auth/Profile'
import {Management} from 'organization/Event/Management'
import AttendantProvider, {
  Attendant,
} from 'organization/Event/Attendants/ViewAttendant/AttendantProvider'
import {Help} from 'organization/Event/Help'
import {OrganizationAutologin} from 'organization/Auth/OrganizationAutologin'
import {PublicPage} from './Event/PublicPage'
import {Certifications} from 'organization/Event/Certification'
import {Products} from 'organization/Event/Products'
import EventProductsProvider from 'organization/Event/Products/EventProductsProvider'
import EventInvitesProvider from 'organization/Event/Attendants/EventInvitesProvider'
import {Integrations} from 'organization/Event/Integrations'
import {Zapier} from 'organization/Event/Integrations/Zapier'
import {CsvImport} from 'organization/Event/Integrations/CsvImport'
import CsvImportProvider from 'organization/Event/Integrations/CsvImport/CsvImportProvider'
import {Invoices} from 'organization/Invoices'
import {OrganizationSettings} from 'organization/OrganizationSettings'
import OrganizationInvoicesProvider from 'organization/Invoices/Invoices/OrganizationInvoicesProvider'
import OrganizationTransactionsProvider from 'organization/Invoices/Transactions/OrganizationTransactionsProvider'
import InvoicesModalsProvider from 'organization/Invoices/InvoicesModalsProvider'
import EventAddonsProvider from 'organization/Event/Addons/EventAddonsProvider'
import {AffiliateProgram} from 'organization/AffiliateProgram'
import StripeConnectionProvider from 'organization/OrganizationSettings/StripeConnectionProvider'
import StripeConnectedCallbackHandler from 'organization/OrganizationSettings/StripeConnectedCallbackHandler'
import {OrganizationEarning} from 'organization/Earning'
import EarningProvider from 'organization/Earning/EarningProvider'
import EarningInvoiceProvider from 'organization/Earning/EarningInvoiceProvider'
import AffiliateEarningProvider from 'organization/AffiliateProgram/AffiliateEarningProvider'
import {OrganizationHelp} from 'organization/Help'
import {TranslationEditor} from 'organization/TranslationEditor/index'
import {CreateEventOnboarding} from 'organization/CreateEventOnboarding'
import {LoggedInNewEventEntry} from 'organization/CreateEventOnboarding/LoggedInNewEventEntry'
export function OrganizationRoutes() {
  const {user} = useOrganizationAuth()
  const urlHasOrganizationID = useURLHasOrganizationID()
  const routesLocation = useRoutesLocationUnderLocale()

  if (!user) {
    return (
      <>
      <Helmet>
        <meta name="robots" content="noindex,follow" />
      </Helmet>
      <Routes location={routesLocation}>
        <Route
          path={pathForRoute(routes.organization.newEvent)}
          element={<CreateEventOnboarding />}
        />
        <Route
          path={pathForRoute(routes.organization.autologin)}
          element={<OrganizationAutologin />}
        />
        <Route
          path={pathForRoute(routes.organization.login)}
          element={<Login />}
        />

        <Route
          path={pathForRoute(routes.organization.register)}
          element={<Register />}
        />

        <Route
          path={pathForRoute(routes.organization.verifyEmail)}
          element={<OrganizationVerifyEmail />}
        />

        <Route
          path={pathForRoute(routes.organization.auth.google)}
          element={<GoogleCallback />}
        />

        <Route
          path={pathForRoute(routes.organization.passwordRecover)}
          element={<PasswordRecover />}
        />

        <Route
          path="*"
          element={<NavigateLocalized to={routes.organization.login} replace />}
        />
      </Routes>
      </>
    )
  }

  if (!urlHasOrganizationID) {
    return (
      <>
      <Helmet>
        <meta name="robots" content="noindex,follow" />
      </Helmet>
      <Routes location={routesLocation}>
        <Route
          path={pathForRoute(routes.organization.newEvent)}
          element={
            <OrganizationsProvider>
              <LoggedInNewEventEntry />
            </OrganizationsProvider>
          }
        />

        <Route
          path={pathForRoute(routes.organization.profile)}
          element={<Profile />}
        />

        <Route
          path={pathForRoute(routes.organization.billing)}
          element={<Billing />}
        />

        <Route
          path={pathForRoute(routes.organization.i18nStudio)}
          element={<TranslationEditor />}
        />

        <Route
          path={pathForRoute(routes.organization.list)}
          element={
            <OrganizationsProvider>
              <List />
            </OrganizationsProvider>
          }
        />

        <Route
          path="*"
          element={<NavigateLocalized to={routes.organization.list} replace />}
        />
      </Routes>
      </>
    )
  }

  return (
    <OrganizationProvider>
      <StripeConnectionProvider>
        <>
        <Helmet>
          <meta name="robots" content="noindex,follow" />
        </Helmet>
        <Routes location={routesLocation}>
          <Route
            path={pathForRoute(
              routes.organization[':organizationId'].event[':eventId']
                .attendant[':attendantId'].view,
            )}
            element={
              <OrganizationEventProvider>
                <AttendantProvider>
                  <ViewAttendant />
                </AttendantProvider>
              </OrganizationEventProvider>
            }
          />

          <Route
            path={pathForRoute(routes.organization[':organizationId'].events)}
            element={
              <OrganizationEventsListProvider>
                <EventsList />
              </OrganizationEventsListProvider>
            }
          />

          <Route
            path={pathForRoute(routes.organization[':organizationId'].earnings)}
            element={
              <EarningProvider>
                <EarningInvoiceProvider>
                  <OrganizationEarning />
                </EarningInvoiceProvider>
              </EarningProvider>
            }
          />

          <Route
            path={pathForRoute(routes.organization[':organizationId'].invoices)}
            element={
              <OrganizationInvoicesProvider>
                <OrganizationTransactionsProvider>
                  <InvoicesModalsProvider>
                    <Invoices />
                  </InvoicesModalsProvider>
                </OrganizationTransactionsProvider>
              </OrganizationInvoicesProvider>
            }
          />

          <Route
            path={pathForRoute(routes.organization[':organizationId'].help)}
            element={<OrganizationHelp />}
          />

          <Route
            path={pathForRoute(
              routes.organization[':organizationId'].settings.root,
            )}
            element={<OrganizationSettings />}
          />

          <Route
            path={pathForRoute(
              routes.organization[':organizationId'].settings.connection[
                ':stripeAccountId'
              ].connected,
            )}
            element={<StripeConnectedCallbackHandler />}
          />

          <Route
            path={pathForRoute(
              routes.organization[':organizationId'].affiliateProgram,
            )}
            element={
              <AffiliateEarningProvider>
                <AffiliateProgram />
              </AffiliateEarningProvider>
            }
          />

          <Route
            path={pathForRoute(
              routes.organization[':organizationId'].event.create.root,
            )}
            element={<CreateEvent />}
          />

          <Route
            path={pathForRoute(
              routes.organization[':organizationId'].event[':eventId']
                .attendants,
            )}
            element={
              <OrganizationEventProvider>
                <EventAttendantsProvider>
                  <EventInvitesProvider>
                    <Attendants />
                  </EventInvitesProvider>
                </EventAttendantsProvider>
              </OrganizationEventProvider>
            }
          />

          <Route
            path={pathForRoute(
              routes.organization[':organizationId'].event[':eventId']
                .management,
            )}
            element={
              <OrganizationEventProvider>
                <Management />
              </OrganizationEventProvider>
            }
          />

          <Route
            path={pathForRoute(
              routes.organization[':organizationId'].event[':eventId'].page,
            )}
            element={
              <OrganizationEventProvider>
                <EventDesign />
              </OrganizationEventProvider>
            }
          />

          <Route
            path={pathForRoute(
              routes.organization[':organizationId'].event[':eventId']
                .publicPage,
            )}
            element={
              <OrganizationEventProvider>
                <PublicPage />
              </OrganizationEventProvider>
            }
          />

          <Route
            path={pathForRoute(
              routes.organization[':organizationId'].event[':eventId'].tickets,
            )}
            element={
              <OrganizationEventProvider>
                <Tickets />
              </OrganizationEventProvider>
            }
          />

          <Route
            path={pathForRoute(
              routes.organization[':organizationId'].event[':eventId'].earning,
            )}
            element={
              <OrganizationEventProvider>
                <EventEarningProvider>
                  <Earning />
                </EventEarningProvider>
              </OrganizationEventProvider>
            }
          />

          <Route
            path={pathForRoute(
              routes.organization[':organizationId'].event[':eventId'].products,
            )}
            element={
              <OrganizationEventProvider>
                <EventProductsProvider>
                  <Products />
                </EventProductsProvider>
              </OrganizationEventProvider>
            }
          />

          <Route
            path={pathForRoute(
              routes.organization[':organizationId'].event[':eventId']
                .cetrifications,
            )}
            element={
              <OrganizationEventProvider>
                <Certifications />
              </OrganizationEventProvider>
            }
          />

          <Route
            path={pathForRoute(
              routes.organization[':organizationId'].event[':eventId']
                .integrations.root,
            )}
            element={
              <OrganizationEventProvider>
                <Integrations />
              </OrganizationEventProvider>
            }
          />

          <Route
            path={pathForRoute(
              routes.organization[':organizationId'].event[':eventId']
                .integrations.zapier,
            )}
            element={
              <OrganizationEventProvider>
                <Zapier />
              </OrganizationEventProvider>
            }
          />

          <Route
            path={pathForRoute(
              routes.organization[':organizationId'].event[':eventId']
                .integrations.csvImport,
            )}
            element={
              <OrganizationEventProvider>
                <CsvImportProvider>
                  <CsvImport />
                </CsvImportProvider>
              </OrganizationEventProvider>
            }
          />

          <Route
            path={pathForRoute(
              routes.organization[':organizationId'].event[':eventId'].addons,
            )}
            element={
              <OrganizationEventProvider>
                <EventAddonsProvider>
                  <Addons />
                </EventAddonsProvider>
              </OrganizationEventProvider>
            }
          />

          <Route
            path={pathForRoute(
              routes.organization[':organizationId'].event[':eventId'].settings,
            )}
            element={
              <OrganizationEventProvider>
                <Settings />
              </OrganizationEventProvider>
            }
          />

          <Route
            path={pathForRoute(
              routes.organization[':organizationId'].event[':eventId'].help,
            )}
            element={
              <OrganizationEventProvider>
                <Help />
              </OrganizationEventProvider>
            }
          />

          <Route
            path={pathForRoute(
              routes.organization[':organizationId'].event[':eventId'].root,
            )}
            element={
              <OrganizationEventProvider>
                <EventIndex />
              </OrganizationEventProvider>
            }
          />

          <Route
            path="*"
            element={
              <NavigateLocalized to={routes.organization.list} replace />
            }
          />
        </Routes>
        </>
      </StripeConnectionProvider>
    </OrganizationProvider>
  )
}

export function useOrganizationRoutes() {
  const id = useParamOrganizationId()

  return routesWithValue(
    ':organizationId',
    String(id),
    routes.organization[':organizationId'],
  )
}

export function useOrganizationRoutesStatic(organizationId: number) {
  return routesWithValue(
    ':organizationId',
    String(organizationId),
    routes.organization[':organizationId'],
  )
}

export function useParamOrganizationId() {
  const {organizationId} = useParams<{organizationId: string}>()
  return organizationId
}

export function useOrganizationEventRoutes() {
  const id = useParamEventId()
  const routes = useOrganizationRoutes()
  return routesWithValue(':eventId', String(id), routes.event[':eventId'])
}

export function useParamEventId() {
  const {eventId} = useParams<{eventId: string}>()
  return eventId
}

export function organizationRoutes(organization: Pick<Organization, 'id'>) {
  return routesWithValue(
    ':organizationId',
    String(organization.id),
    routes.organization[':organizationId'],
  )
}

export function organizationEventRoutes(
  event: Pick<EventModel, 'id'>,
  organizationRoutes: any,
) {
  return routesWithValue(
    ':eventId',
    String(event.id),
    organizationRoutes.event[':eventId'],
  )
}

export function organizationEventAttendeeRoutes(
  attendant: Pick<Attendant, 'id'>,
  organizationEventRoutes: any,
) {
  return routesWithValue(
    ':attendantId',
    String(attendant.id),
    organizationEventRoutes.attendant[':attendantId'],
  )
}
